import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import apiClient from "../api/apiClient.js";

// Vérification que createContext existe
if (!React.createContext) {
  React.createContext = (defaultValue) => {
    const context = {
      Provider: ({ value, children }) => {
        context._currentValue = value;
        return children;
      },
      Consumer: ({ children }) => children(context._currentValue),
      _currentValue: defaultValue,
    };
    return context;
  };
}

// Création du contexte
const UserContext = createContext();

// Hook personnalisé pour accéder au contexte
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(
      "useUser doit être utilisé à l'intérieur d'un UserProvider",
    );
  }
  return context;
};

// Fournisseur du contexte
export const UserProvider = ({ children }) => {
  const { user: authUser, isAuthenticated } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charger le profil utilisateur lorsque l'authentification change
  useEffect(() => {
    if (isAuthenticated && authUser) {
      fetchUserProfile();
    } else {
      setUserProfile(null);
    }
  }, [isAuthenticated, authUser]);

  // Fonction pour récupérer le profil complet de l'utilisateur
  const fetchUserProfile = async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      // Appel à l'API pour récupérer les données complètes du profil
      const response = await apiClient.get('/auth/me');

      if (response && response.data) {
        setUserProfile(response.data);
      }
    } catch (err) {
      console.error(
        "Erreur lors de la récupération du profil utilisateur:",
        err,
      );
      setError("Impossible de charger le profil utilisateur");
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour le profil utilisateur
  const updateProfile = async (profileData) => {
    if (!isAuthenticated || !authUser?.id) return null;

    try {
      setLoading(true);
      setError(null);

      // Utiliser le endpoint approprié trouvé dans l'API
      const response = await apiClient.put('/users/profile', profileData);

      if (response && response.data) {
        setUserProfile(response.data);
        return response.data;
      }
      return null;
    } catch (err) {
      console.error("Erreur lors de la mise à jour du profil:", err);
      setError("Impossible de mettre à jour le profil");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Valeur du contexte
  const value = {
    userProfile,
    loading,
    error,
    fetchUserProfile,
    updateProfile,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
